/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, October 23rd 2021, 1:19:30 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { PrimaryKeyValue, QueryParameters } from "lib/types";
import { useAPIRequest, UseAPIRequestArgs, APIMakeRequestFncArgs } from "lib/api/hooks";
import React from "react";

/**
 *
 * Use this hook when you want to get an item from an API Get endpoint with id as a primary key.
 *
 * @param apiServicePath The API Service to call
 * @param id The id for the item being fetched. This maps to a Get Single item resource. Can be left blank if the API does not need the id set.
 * @param dependencyList The list of items that should result in any useEffect hooks running during a re-render. Dependency list must always be a list of either primitive values or memoized objects
 * @returns the item object and makeRequest hook state i.e. isMakingAPIRequest and isAPIError
 *
 */
export interface FetchItemArgs<T>
  extends UseAPIRequestArgs<T>,
    Pick<APIMakeRequestFncArgs<T>, "apiQueryParams" | "apiPathParams"> {
  dependencyList?: any[];
}

type FetchItemFncOptions<T> = Omit<APIMakeRequestFncArgs<T>, "setValidAPIResponse">;

export const useAPIFetchItem = <T>(args: FetchItemArgs<T>) => {
  const { dependencyList = [], ...useAPIRequestArgs } = args;

  const { apiPathParams = [], apiQueryParams = {} } = useAPIRequestArgs;

  const [apiItem, setAPIItem] = React.useState<T>(null);

  // Fetch item afresh whenever passed dependencies change, otherwise, the empty dependency list will just fetch the item on the first render only.
  const { makeRequest, makeRequestCleanUp, ...othermakeRequestHookValues } = useAPIRequest<T>({ ...useAPIRequestArgs });

  // When we just want to fetch the item immediately the calling component is mounted.
  React.useEffect(() => {
    if (useAPIRequestArgs.makeRequestOnInitialMount) return fetchItem();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencyList]);

  // The fetch function can be called outside of the above useEffect. Gives flexibility for components to fetchItem at different points and even use their own useEffects.
  const fetchItem = React.useCallback(
    (id: PrimaryKeyValue<T> = null, options?: FetchItemFncOptions<T>) => {
      const resolvedPathParams = [...apiPathParams, ...(options?.apiPathParams ?? [])];

      const resolvedQueryParams = { ...apiQueryParams, ...options?.apiQueryParams } as QueryParameters<T>;

      return makeRequest({
        ...useAPIRequestArgs,
        apiPathParams: id ? [id, ...resolvedPathParams] : resolvedPathParams,
        apiQueryParams: resolvedQueryParams,
        setValidAPIResponse: setAPIItem,
      });
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest, JSON.stringify(apiPathParams), JSON.stringify(apiQueryParams)]
  );

  return {
    apiItem,
    fetchItem,
    ...othermakeRequestHookValues,
  };
};
